// REQ-P1-06b: 管理者グループチャットの契約型付きAPI。

import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type AdminGroupChatThreadsQuery = NonNullable<
  paths['/admin/group-chat/threads']['get']['parameters']['query']
>;
type AdminGroupChatThreadsResponse = NonNullable<
  paths['/admin/group-chat/threads']['get']['responses'][200]['content']
>['application/json'];
type AdminGroupChatMessagesResponse = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/messages/{offset}/{limit}']['get']['responses'][200]['content']
>['application/json'];
type AdminGroupChatMessagesAroundResponse = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/messages/around/{messageId}/{before}/{after}']['get']['responses'][200]['content']
>['application/json'];
type AdminGroupChatThreadResponse = NonNullable<
  paths['/admin/group-chat/threads/{threadId}']['get']['responses'][200]['content']
>['application/json'];
type AdminGroupChatMembersResponse = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/members']['get']['responses'][200]['content']
>['application/json'];
type CreateAdminGroupChatThreadRequest = NonNullable<
  paths['/admin/group-chat/threads']['post']['requestBody']
>['content']['application/json'];
type CreateAdminGroupChatThreadResponse = NonNullable<
  paths['/admin/group-chat/threads']['post']['responses'][201]['content']
>['application/json'];
type UpdateAdminGroupChatThreadSettingsRequest = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/settings']['patch']['requestBody']
>['content']['application/json'];
type AddAdminGroupChatMembersRequest = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/members']['post']['requestBody']
>['content']['application/json'];
type AdminGroupChatThreadIconUploadRequest = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/icon']['put']['requestBody']
>['content']['multipart/form-data'];
type AdminGroupChatNotifyMuteResponse = NonNullable<
  paths['/admin/group-chat/notify-mute/{threadId}']['get']['responses'][200]['content']
>['application/json'];
type UpdateAdminGroupChatNotifyMuteRequest = NonNullable<
  paths['/admin/group-chat/notify-mute']['post']['requestBody']
>['content']['application/json'];
type AdminGroupChatUnreadCountResponse = NonNullable<
  paths['/admin/group-chat/unread-count']['get']['responses'][200]['content']
>['application/json'];
type AdminGroupChatTextMessageRequest = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/messages/text']['post']['requestBody']
>['content']['application/json'];
type AdminGroupChatImageUploadRequest = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/messages/images']['post']['requestBody']
>['content']['multipart/form-data'];
type AdminGroupChatFileUploadRequest = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/messages/files']['post']['requestBody']
>['content']['multipart/form-data'];
type AdminGroupChatMessageReadsResponse = NonNullable<
  paths['/admin/group-chat/threads/{threadId}/reads/{messageId}']['get']['responses'][200]['content']
>['application/json'];

function ensureOk(status: number, ok: boolean) {
  if (!ok) throw new ApiStatusError(status);
}

function ensureData<T>(data: T | undefined, status: number, ok: boolean): T {
  if (!ok || !data) throw new ApiStatusError(status);
  return data;
}

async function readOptionalJson(data: unknown, response: Response) {
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch (_error) {
    return {};
  }
}

async function ensureOkWithPayloadMessage(response: Response, payload: Record<string, unknown>) {
  if (response.ok) return;
  throw new Error(String(payload?.message || payload?.error || `HTTP ${response.status}`));
}

async function ensurePayloadOk(data: unknown, response: Response) {
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload;
}

export async function listAdminGroupChatThreads(
  query?: AdminGroupChatThreadsQuery
): Promise<AdminGroupChatThreadsResponse> {
  const { data, response } = await api.GET('/admin/group-chat/threads', {
    params: query ? { query } : undefined,
  });
  return ensureData(data as AdminGroupChatThreadsResponse | undefined, response.status, response.ok);
}

export async function getAdminGroupChatThread(threadId: string): Promise<AdminGroupChatThreadResponse> {
  const { data, response } = await api.GET('/admin/group-chat/threads/{threadId}', {
    params: { path: { threadId } },
  });
  return ensureData(data as AdminGroupChatThreadResponse | undefined, response.status, response.ok);
}

export async function listAdminGroupChatMembers(
  threadId: string
): Promise<AdminGroupChatMembersResponse | null> {
  const { data, response } = await api.GET('/admin/group-chat/threads/{threadId}/members', {
    params: { path: { threadId } },
  });
  if (!response.ok || !data) return null;
  return data as AdminGroupChatMembersResponse;
}

export async function createAdminGroupChatThread(body: CreateAdminGroupChatThreadRequest) {
  const { data, response } = await api.POST('/admin/group-chat/threads', { body });
  return ensureData(data as CreateAdminGroupChatThreadResponse | undefined, response.status, response.ok);
}

export async function updateAdminGroupChatThreadSettings(
  threadId: string,
  body: UpdateAdminGroupChatThreadSettingsRequest
) {
  const { response } = await api.PATCH('/admin/group-chat/threads/{threadId}/settings', {
    params: { path: { threadId } },
    body,
  });
  ensureOk(response.status, response.ok);
}

export async function deleteAdminGroupChatThread(threadId: string) {
  const { response } = await api.DELETE('/admin/group-chat/threads/{threadId}', {
    params: { path: { threadId } },
  });
  ensureOk(response.status, response.ok);
}

export async function closeAdminGroupChatThread(threadId: string) {
  const { response } = await api.POST('/admin/group-chat/threads/{threadId}/close', {
    params: { path: { threadId } },
  });
  ensureOk(response.status, response.ok);
}

export async function openAdminGroupChatThread(threadId: string) {
  const { response } = await api.POST('/admin/group-chat/threads/{threadId}/open', {
    params: { path: { threadId } },
  });
  ensureOk(response.status, response.ok);
}

export async function listAdminGroupChatMessages(
  threadId: string,
  offset: number,
  limit: number
): Promise<AdminGroupChatMessagesResponse> {
  const { data, response } = await api.GET(
    '/admin/group-chat/threads/{threadId}/messages/{offset}/{limit}',
    {
      params: { path: { threadId, offset, limit } },
    }
  );
  return ensureData(data as AdminGroupChatMessagesResponse | undefined, response.status, response.ok);
}

export async function getAdminGroupChatMessagesAround(
  threadId: string,
  messageId: string,
  before: number,
  after: number
): Promise<AdminGroupChatMessagesAroundResponse> {
  const { data, response } = await api.GET(
    '/admin/group-chat/threads/{threadId}/messages/around/{messageId}/{before}/{after}',
    {
      params: { path: { threadId, messageId, before, after } },
    }
  );
  return ensureData(data as AdminGroupChatMessagesAroundResponse | undefined, response.status, response.ok);
}

export async function markAdminGroupChatMessagesRead(body: Record<string, unknown>) {
  const { response } = await api.POST('/admin/group-chat/read', { body });
  ensureOk(response.status, response.ok);
}

export async function listAdminGroupChatMessageReads(
  threadId: string,
  messageId: string
): Promise<AdminGroupChatMessageReadsResponse> {
  const { data, response } = await api.GET('/admin/group-chat/threads/{threadId}/reads/{messageId}', {
    params: { path: { threadId, messageId } },
  });
  return ensurePayloadOk(data, response) as Promise<AdminGroupChatMessageReadsResponse>;
}

export async function sendAdminGroupChatTextMessage(
  threadId: string,
  body: AdminGroupChatTextMessageRequest
) {
  const { data, response } = await api.POST(
    '/admin/group-chat/threads/{threadId}/messages/text',
    {
      params: { path: { threadId } },
      body,
    }
  );
  return ensurePayloadOk(data, response);
}

export async function sendAdminGroupChatImageMessages(
  threadId: string,
  body: AdminGroupChatImageUploadRequest
) {
  const { data, response } = await api.POST(
    '/admin/group-chat/threads/{threadId}/messages/images',
    {
      params: { path: { threadId } },
      body,
    }
  );
  return ensurePayloadOk(data, response);
}

export async function sendAdminGroupChatFileMessage(
  threadId: string,
  body: AdminGroupChatFileUploadRequest
) {
  const { data, response } = await api.POST(
    '/admin/group-chat/threads/{threadId}/messages/files',
    {
      params: { path: { threadId } },
      body,
    }
  );
  return ensurePayloadOk(data, response);
}

export async function getAdminGroupChatUnreadCount(): Promise<AdminGroupChatUnreadCountResponse> {
  const { data, response } = await api.GET('/admin/group-chat/unread-count');
  return ensureData(data as AdminGroupChatUnreadCountResponse | undefined, response.status, response.ok);
}

export async function getAdminGroupChatNotifyMute(
  threadId: string
): Promise<AdminGroupChatNotifyMuteResponse> {
  const { data, response } = await api.GET('/admin/group-chat/notify-mute/{threadId}', {
    params: { path: { threadId } },
  });
  return ensureData(data as AdminGroupChatNotifyMuteResponse | undefined, response.status, response.ok);
}

export async function updateAdminGroupChatNotifyMute(
  body: UpdateAdminGroupChatNotifyMuteRequest
) {
  const { response } = await api.POST('/admin/group-chat/notify-mute', { body });
  ensureOk(response.status, response.ok);
}

export async function addAdminGroupChatMembers(
  threadId: string,
  body: AddAdminGroupChatMembersRequest
) {
  const { response } = await api.POST('/admin/group-chat/threads/{threadId}/members', {
    params: { path: { threadId } },
    body,
  });
  ensureOk(response.status, response.ok);
}

export async function deleteAdminGroupChatMember(threadId: string, artistId: string) {
  const { response } = await api.DELETE('/admin/group-chat/threads/{threadId}/members/{artistId}', {
    params: { path: { threadId, artistId } },
  });
  ensureOk(response.status, response.ok);
}

export async function deleteAdminGroupChatMessage(threadId: string, messageId: string) {
  const { data, response } = await api.DELETE(
    '/admin/group-chat/threads/{threadId}/messages/{messageId}',
    {
      params: { path: { threadId, messageId } },
    }
  );
  ensureOk(response.status, response.ok);
  return readOptionalJson(data, response);
}

export async function uploadAdminGroupChatThreadIcon(
  threadId: string,
  body: AdminGroupChatThreadIconUploadRequest
) {
  const { response } = await api.PUT('/admin/group-chat/threads/{threadId}/icon', {
    params: { path: { threadId } },
    body,
  });
  ensureOk(response.status, response.ok);
}

export async function deleteAdminGroupChatThreadIcon(threadId: string) {
  const { response } = await api.DELETE('/admin/group-chat/threads/{threadId}/icon', {
    params: { path: { threadId } },
  });
  ensureOk(response.status, response.ok);
}
